import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './components.css';
import Branding from './Branding';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const response = await axios.post('/api/auth/request-password-reset', {
        email
      });

      // Track password reset request in PostHog
      try {
        posthog.capture('password_reset_requested', {
          email_domain: email.split('@')[1]
        });
      } catch (err) {
        console.error('PostHog tracking error:', err);
      }

      setMessage(response.data.message);
      setLoading(false);
    } catch (error) {
      console.error('Password reset request error:', error);
      setError(error.response?.data?.error || 'An error occurred');
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    // Track return to login in PostHog
    try {
      posthog.capture('returned_to_login_from_forgot_password');
    } catch (err) {
      console.error('PostHog tracking error:', err);
    }
    
    navigate('/login');
  };

  return (
    <div className="login-container" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: Branding.gradients.headerGradient,
      padding: Branding.spacing.md
    }}>
      <div className="login-card" style={{
        backgroundColor: Branding.colors.pureWhite,
        borderRadius: Branding.borders.radius.lg,
        boxShadow: Branding.shadows.lg,
        padding: Branding.spacing.xl,
        width: '100%',
        maxWidth: '440px'
      }}>
        <h2 style={{
          color: Branding.colors.deepPurple,
          marginBottom: Branding.spacing.xs,
          textAlign: 'center',
          fontWeight: Branding.typography.fontWeight.bold,
          fontSize: Branding.typography.fontSize['2xl']
        }}>Reset Your Password</h2>
        <p style={{
          color: Branding.colors.slateGray,
          textAlign: 'center',
          marginBottom: Branding.spacing.lg,
          fontSize: Branding.typography.fontSize.base
        }}>
          Enter your email address and we'll send you a link to reset your password.
        </p>
        
        {error && <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: Branding.borders.radius.md,
          color: '#b91c1c',
          padding: Branding.spacing.md,
          marginBottom: Branding.spacing.md,
          fontSize: Branding.typography.fontSize.sm
        }}>{error}</div>}
        
        {message && <div style={{
          backgroundColor: '#dcfce7',
          border: '1px solid #22c55e',
          borderRadius: Branding.borders.radius.md,
          color: '#15803d',
          padding: Branding.spacing.md,
          marginBottom: Branding.spacing.md,
          fontSize: Branding.typography.fontSize.sm
        }}>{message}</div>}
        
        <form onSubmit={handleSubmit}>
          <div style={{
            marginBottom: Branding.spacing.lg,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <label htmlFor="email" style={{
              display: 'block',
              marginBottom: Branding.spacing.xs,
              fontWeight: Branding.typography.fontWeight.medium,
              color: Branding.colors.darkSlate,
              fontSize: Branding.typography.fontSize.sm
            }}>Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: Branding.spacing.md,
                border: `1px solid ${Branding.colors.veryLightGray}`,
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                transition: Branding.transitions.default
              }}
              placeholder="Enter your email address"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={loading}
            style={{
              width: '100%',
              padding: Branding.spacing.md,
              background: Branding.gradients.timerGradient,
              color: Branding.colors.pureWhite,
              border: 'none',
              borderRadius: Branding.borders.radius.md,
              fontSize: Branding.typography.fontSize.base,
              fontWeight: Branding.typography.fontWeight.semibold,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: Branding.transitions.default,
              boxShadow: Branding.shadows.md,
              opacity: loading ? 0.8 : 1
            }}
          >
            {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
          </button>
        </form>
        
        <div style={{
          marginTop: Branding.spacing.xl,
          textAlign: 'center'
        }}>
          <button 
            type="button"
            onClick={handleBackToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: Branding.colors.tealBlue,
              cursor: 'pointer',
              fontWeight: Branding.typography.fontWeight.medium,
              fontSize: Branding.typography.fontSize.sm,
              textDecoration: 'underline'
            }}
          >
            ← Back to Login
          </button>
        </div>
        
        <div style={{
          marginTop: Branding.spacing.lg,
          fontSize: Branding.typography.fontSize.xs,
          color: Branding.colors.mutedMauve,
          textAlign: 'center'
        }}>
          <p>For security reasons, we'll only send reset links to registered email addresses.</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;